/** Canonical pure semantics, shared by the host and native editor. */
import type { SceneGenMode } from '../types/scene-gen-mode';

// 🎬 **Omni Flash(Gemini) 컷 계약의 순수부** — 지식쇼츠 장르 레인(2026-08-21).
//
// ★왜 lib 로 뺐나★: `cinematicCanvasGeneration.ts` 는 supabase 클라이언트를 import 하는 3,700줄
//   모듈이라 vitest 가 값을 직접 단언하기 어렵다. 이 레포는 그런 자리에서 "소스 문자열 검사"로
//   때우다 두 번 위장 green 을 냈다(`_shared/video-text-guard.ts:76-81` 실측 박제). 판정을 순수
//   함수로 접으면 **반환값**을 직접 단언할 수 있고 축이 갈릴 자리가 없다.
//
// ★계약 정본은 EF 쪽 `supabase/functions/_shared/gemini-omni-contract.ts` 다★ — 이 파일은 그
//   화이트리스트를 **프론트에서 미리 좁히는** 사본이다. 두 벌인 이유: EF 가 400 으로 거절하는 값을
//   유저가 화면에서 고를 수 있으면, 유저는 조치 불가능한 에러를 본다. 프론트가 먼저 좁히면 정상
//   UI 에서는 그 400 에 도달하지 않는다(우회 방어로만 남는다).
//   ★한쪽을 고치면 다른 쪽도 본다★ — 교차 계약 테스트(`omni-cut-contract.test.ts`)가 대조한다.

/**
 * 🧪🔓 **Omni 자유 모드 — 프론트 사본**(회장님 지시 2026-08-22 · 테스트 기간 한정).
 *
 * ★정본은 EF `supabase/functions/_shared/gemini-omni-contract.ts` 의 `OMNI_FREE_MODE` 다★ —
 *   근거 실측·되돌리기 절차가 그 주석에 있다. 여기는 **프론트가 읽는 사본**이고, 교차 대조
 *   테스트(`omni-cut-contract.test.ts`)가 두 값이 갈리는 것을 막는다.
 *
 * ★프론트에서 이 값이 끄는 것★: 무발화 관용구 주입. Omni 는 오디오 스위치가 없어 **항상** 소리를
 *   만드는데, 지식쇼츠(나레이션 별도 트랙)를 전제로 "말하지 마라"를 심어 두면 **발화 스타일이
 *   통째로 벙어리가 된다**(퀴즈쇼 등 39종 중 29종이 인클립 발화다). 회장님 실측에서 Omni 발화가
 *   시댄스에 뒤지지 않는 것이 확인돼 이 주입을 자유 모드에서 걷는다.
 * ★★스튜디오 스크럽은 이 값과 무관하게 항상 걸린다★★ — 회장님 확정(2026-08-22). 그건 품질이 아니라
 *   벤더 거절 방지 장치라, 풀면 유료 실패가 는다.
 */
export const OMNI_FREE_MODE = true;

export const OMNI_MAX_SEC = 10;


/**
 * 🎛 `genMode` 의 옴니 값 — ★읽기(`isOmniCut`)와 쓰기(셀렉트 `value`)가 같은 상수를 쓴다★(리뷰 3R P3):
 *   쓰기 쪽만 맨 리터럴이면 값 개칭 시 셀렉트만 조용히 갈려 팁·고지가 통째로 안 뜬다(셀렉트 value 는
 *   타입 검사가 물지 않는 자리다).
 * ★`satisfies` 로 유니온에 결박★(리뷰 4R P3): 유니온 쪽 값을 개칭하면 이 상수만 조용히 낡는 경로를
 *   tsc 가 빌드째 깨뜨리게 한다(타입 전용 import — 런타임 의존 0).
 */
export const OMNI_GEN_MODE = "omni" as const satisfies NonNullable<
  SceneGenMode
>;


/**
 * 🎛 이 컷이 Omni 경로인가 — `genMode` 단독 판정.
 * ★`deviceCode` 를 보지 않는 것이 계약★: `"auto"` 의 L→HeyGen 자동분기와 달리 Omni 는 **명시 선택
 *   전용**이다. 자동으로 흘러들면 유저가 고른 적 없는 벤더로 과금이 나간다.
 */
export function isOmniCut(genMode: unknown): boolean {
  return genMode === OMNI_GEN_MODE;
}


/**
 * 🎛 `genMode` 의 **Omni 1.1** 값 — `OMNI_GEN_MODE` 의 형제(같은 `satisfies` 결박·같은 이유).
 * ★1.0 상수를 재사용하지 않는다★: 두 세대는 EF·요청 형상·강점이 갈려 있고, 값이 한 상수를 공유하면
 *   한쪽만 개칭·롤백하는 날 다른 쪽이 조용히 따라간다.
 */
export const OMNI11_GEN_MODE = "omni11" as const satisfies NonNullable<
  SceneGenMode
>;


/**
 * 🎛 이 컷이 Omni **1.1** 경로인가 — `genMode` 단독 판정(`isOmniCut` 과 같은 계약).
 *
 * ★★`isOmniCut` 과 합치지 마라★★ — 두 세대를 한 술어로 접으면 ①라우팅이 어느 EF 로 갈지 못 가르고
 *   ②1.0 의 자유 모드·프롬프트 관용구가 1.1 요청에 그대로 실리며 ③한쪽을 되돌리는 커밋이 다른 쪽까지
 *   끈다. EF 가 `OMNI11_FREE_MODE` 를 `OMNI_FREE_MODE` 와 **다른 상수**로 세운 것과 같은 극성이고,
 *   `omni-free-mode-isolation` 이 잠근 "두 모델 한 스위치 금지" 계약이 이 축에도 그대로 적용된다.
 * ★`deviceCode` 를 보지 않는 것도 그대로 승계한다★ — 1.1 도 **명시 선택 전용**이다. 자동으로 흘러들면
 *   유저가 고른 적 없는 벤더로 과금이 나간다.
 */
export function isOmni11Cut(genMode: unknown): boolean {
  return genMode === OMNI11_GEN_MODE;
}


/** 🎬 옴니 **계열**(세대 무관) — 견적·어법 등 "두 세대가 같게 받는" 축의 판정. */
export function isOmniFamilyCut(genMode: unknown): boolean {
  return isOmniCut(genMode) || isOmni11Cut(genMode);
}

